import type {
  OddsResponse,
  RequestSimulationRunResponse,
  SimulationRun
} from '@library/types/simulations';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { GameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import type { OverrideValidationError } from '@bo-hooks/simulations/useSimulationDetailSteps';
import type { SerializedError } from '@reduxjs/toolkit';
import type { SimulationBuilder } from '@bo-hooks/simulations/useSimulationBuilder';
import { StepOneBuild } from '@bo-components/simulations/steps/StepOneBuild/StepOneBuild';
import type { Updater } from 'use-immer';

export interface StepTwoResultsProps {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;

  handleRequestSimulationRun: () => Promise<
    | {
        data: RequestSimulationRunResponse;
      }
    | {
        error: FetchBaseQueryError | SerializedError;
      }
  >;
  leagueId: string;
  isSimulationOddsError: boolean;
  isSimulationOddsLoading: boolean;
  overrideValidationErrors: Array<OverrideValidationError>;
  relevantPlayers: Array<LeagueTeamPlayer>;
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
  simulation?: SimulationRun;
  simulationBuilder: SimulationBuilder;
  simulationOdds?: OddsResponse;
}

export const StepTwoResults = ({
  allMarkets,
  game,
  gamePlayers,
  handleRequestSimulationRun,
  leagueId,
  isSimulationOddsError,
  isSimulationOddsLoading,
  overrideValidationErrors,
  setOverrideValidationErrors,
  relevantPlayers,
  simulation = undefined,
  simulationBuilder,
  simulationOdds = undefined
}: StepTwoResultsProps) => {
  const { attributes } = simulation?.data || {};
  const { simulation_run_status: simStatus } = attributes || {};

  if (!simulation) {
    <div>
      <NoResults />
      <span>
        Cannot find this simulation. Please go back to the Simulation Dashboard.
      </span>
    </div>;
  }

  if (simStatus === 'SIMULATION_FAILED') {
    return (
      <div>
        <NoResults />
        <span>
          Your simulation has failed. If you want to make changes and try again,
          go back to the Build step.
        </span>
      </div>
    );
  }

  if (isSimulationOddsError) {
    return <NoResults label="Odds Error" />;
  }

  if (isSimulationOddsLoading || !simulationOdds) {
    return <Loading loadingMessage="Loading Odds" />;
  }

  return (
    <StepOneBuild
      allMarkets={allMarkets}
      game={game}
      gamePlayers={gamePlayers}
      handleRequestSimulationRun={handleRequestSimulationRun}
      leagueId={leagueId}
      overrideValidationErrors={overrideValidationErrors}
      relevantPlayers={relevantPlayers}
      setOverrideValidationErrors={setOverrideValidationErrors}
      simulation={simulation}
      simulationBuilder={simulationBuilder}
      simulationOdds={simulationOdds}
      simulationResults
    />
  );
};
